import React from "react";
import { Link } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import {
  CheckCircle2, Sparkles, Star, MessageCircle, Award, ShieldCheck, BookOpen, FileText,
  Search, Languages, RefreshCw, Users, TrendingUp, Globe, Quote, Lock, Clock, ArrowRight,
  HelpCircle, Zap, AlertCircle,
} from "lucide-react";
import { usePopup } from "../context/PopupContext";
import SignupForm from "../components/SignupForm";
import ChatButton from "../components/ChatButton";
import TestimonialCarousel from "../components/TestimonialCarousel";
import MobileBoxRow from "../components/MobileBoxRow";
import PageMeta from "../components/PageMeta";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

/**
 * Landing Page (/lp)
 * - No full navigation menu (logo left + Create Wiki Page / Chat Now right)
 * - Hero with inline form, informative section below, and 12+ additional
 *   content sections each with their own CTA button.
 */
export default function Landing() {
  const { openPopup } = usePopup();

  const SectionCTA = ({ label = "Reserve my discount", onClick, testId, variant = "primary" }) => {
    const base = "h-11 rounded-full px-6 text-sm font-semibold";
    if (variant === "outline") {
      return (
        <Button onClick={onClick || openPopup} variant="outline" className={base} data-testid={testId}>
          {label}
        </Button>
      );
    }
    return (
      <Button
        onClick={onClick || openPopup}
        className={`${base} text-white`}
        style={{ background: "linear-gradient(135deg,#0A2540 0%,#10B981 165%)" }}
        data-testid={testId}
      >
        {label}
      </Button>
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-slate-900" data-testid="landing-page">
      <PageMeta
        title="Create Your Wikipedia Page — 35% Discount | Prime Wiki Services"
        description="Reserve 35% off your Wikipedia page creation this week. Policy-compliant pages written by senior editors, free notability review, NDA-protected engagement. Published in 4–10 weeks."
        keywords="wikipedia page creation, create wikipedia page, wikipedia services discount, wikipedia agency, wikipedia page writers"
        noindex={false}
      />
      {/* Minimal top bar */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
          <a href="/lp" data-testid="lp-logo" className="shrink-0"><BrandLogo /></a>
          <div className="flex items-center gap-2">
            <Button
              onClick={openPopup}
              className="h-10 rounded-full px-3 text-xs font-semibold text-white sm:px-5 sm:text-sm"
              style={{ background: "linear-gradient(135deg,#0A2540 0%,#10B981 165%)" }}
              data-testid="lp-create-wiki-button"
            >
              <span className="hidden sm:inline">Create Wiki Page</span>
              <span className="sm:hidden">Start</span>
            </Button>
            <ChatButton
              className="h-10 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-[#0A2540] hover:border-slate-300 sm:px-5 sm:text-sm"
              data-testid="lp-chat-now-button"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Chat Now</span>
            </ChatButton>
          </div>
        </div>
      </header>

      {/* HERO + form */}
      <section className="mwb-hero-bg relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" /> Reserve 35% discount · This week only
            </span>
            <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">
              Your Wikipedia page, written by editors Wikipedia trusts.
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
              We create, publish and defend policy-compliant Wikipedia pages for individuals and brands in 42 languages. Free notability review included.
            </p>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2 text-sm text-slate-700">
              {["92% AfC acceptance rate", "400+ pages published", "NDA-protected engagement", "12 months post-publish monitoring", "Refund if not notable", "Published in 4–10 weeks"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />{t}</li>
              ))}
            </ul>
            <div className="mt-7 flex items-center gap-4">
              <div className="flex -space-x-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="grid h-9 w-9 place-items-center rounded-full bg-[#0A2540] font-serif text-sm text-white ring-2 ring-white">{String.fromCharCode(65 + i)}</div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-amber-500">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400" />)}</div>
                <div className="text-xs text-slate-500">4.9/5 from 400+ clients worldwide</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5"><SignupForm testIdPrefix="lp" /></div>
        </div>
      </section>

      {/* Section 1 — Informative intro (removed per user request) */}

      {/* Section 2 — Why Wikipedia? (mirrors homepage) */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="lp-sec-why-wiki">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Why Wikipedia?</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">The most trusted page on the internet about you.</h2>
            <p className="mt-4 text-base text-slate-600">Wikipedia is one of the top-ranked results on Google for personal and brand names. A well-maintained page directly feeds Google's Knowledge Panel, Bing entities and Siri/Alexa answers.</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {["Instant credibility for investor, press and customer research","Drives Google Knowledge Panel and Rich Results visibility","A permanent, policy-protected biographical record","Third-party signal trusted by journalists and analysts"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />{t}</li>
              ))}
            </ul>
            <div className="mt-6"><SectionCTA label="Reserve my discount" testId="lp-cta-whywiki" /></div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80" alt="Senior editorial team reviewing research on multiple screens" className="h-[360px] w-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-xl md:block">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Built on verified sources</div>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500"/><span>Reuters, NYT, WSJ cited</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500"/><span>Peer-reviewed references</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500"/><span>Neutral policy-safe tone</span></div>
              </div>
              <div className="mt-4 flex items-center justify-between rounded-lg bg-[#0A2540] px-3 py-2 text-xs text-white">
                <span>Reliability score</span>
                <span className="font-serif text-base text-emerald-300">9.4 / 10</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — Advantages of Expert Assistance */}
      <section className="bg-slate-50 py-16" data-testid="lp-sec-advantages">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-stretch">
            <div className="order-2 lg:order-1">
              <div className="relative h-full min-h-[420px] overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=1200&q=80"
                  alt="A senior editor reviewing a manuscript with reading glasses — expert editorial oversight"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-tr from-[#0A2540]/35 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Senior editorial lead</div>
                    <div className="font-serif text-base font-semibold text-[#0A2540]">11 years on English Wikipedia</div>
                  </div>
                  <div className="flex -space-x-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="grid h-9 w-9 place-items-center rounded-full bg-[#0A2540] font-serif text-xs text-white ring-2 ring-white">{String.fromCharCode(69 + i)}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 min-w-0 lg:order-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Advantages of Expert Assistance</p>
              <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Why working with a specialist changes the outcome.</h2>
              <p className="mt-4 text-base text-slate-600">Wikipedia is a closed community with its own culture, policies and bureaucratic etiquette. A seasoned editor already knows what triggers reviewers, which sources will be dismissed, and how to phrase a sentence so it survives scrutiny.</p>
              <MobileBoxRow cols="sm:grid-cols-2" gap="gap-4" className="mt-6">
                {[
                  { i: ShieldCheck, t: "Policy fluency", d: "A decade inside WP:GNG, WP:NPOV and WP:COI — we know every tripwire." },
                  { i: Search, t: "Source expertise", d: "We know which outlets reviewers trust and which they dismiss on sight." },
                  { i: FileText, t: "Neutral tone mastery", d: "Editorial writing that reads encyclopedic, never promotional or marketing-style." },
                  { i: Clock, t: "Faster approvals", d: "Avoid endless revision loops — submit right the first time around." },
                ].map((a) => (
                  <div key={a.t} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-50 text-emerald-600"><a.i className="h-4.5 w-4.5" /></div>
                    <div className="mt-3 font-serif text-base font-semibold text-[#0A2540]">{a.t}</div>
                    <div className="mt-1 text-sm text-slate-600">{a.d}</div>
                  </div>
                ))}
              </MobileBoxRow>
              <div className="mt-6"><SectionCTA label="Talk to an expert" testId="lp-cta-advantages" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 — Our Services (redesigned, professional, well-structured) */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="lp-sec-services">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Our Services</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Four focused services covering every Wikipedia need.</h2>
          <p className="mt-4 text-base text-slate-600">From a brand-new page to long-term defence, every engagement is handled by a senior Wikipedian — start to finish, start to renewal.</p>
        </div>
        <MobileBoxRow cols="md:grid-cols-2" gap="gap-6" className="mt-12">
          {[
            { i: BookOpen, num: "01", t: "Wiki Creation & Setup", d: "End-to-end build of a new Wikipedia page — from notability audit to live publication.", bullets: ["Written notability eligibility report", "Senior editor assigned to your project", "AfC submission handled for you", "Published in 4–10 weeks"], cta: "Start a new page" },
            { i: FileText, num: "02", t: "Content Writing & Editing", d: "Rescue a reverted draft or rewrite weak copy into neutral, defendable prose.", bullets: ["Tone-first rewrite to WP:NPOV", "Citation audit and upgrade", "Redlined draft with rationale", "Two revision rounds included"], cta: "Fix my draft" },
            { i: Search, num: "03", t: "Compliance & Notability", d: "Before you commit — a written verdict on whether you qualify for Wikipedia.", bullets: ["WP:GNG / WP:BIO assessment", "Source gap analysis", "Risk rating and PR roadmap", "3–5 business day turnaround"], cta: "Request an audit" },
            { i: RefreshCw, num: "04", t: "Maintenance & Translation", d: "Keep your live page accurate, defended and republished across global audiences.", bullets: ["24/7 page monitoring and alerts", "Vandalism defence", "Quarterly content refresh sprints", "42 Wikipedia languages supported"], cta: "Protect my page" },
          ].map((s) => (
            <div
              key={s.t}
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_30px_60px_-25px_rgba(16,185,129,0.45)]"
              data-testid={`lp-service-${s.num}`}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600"
              />
              <div className="flex items-start justify-between">
                <div
                  className="grid h-14 w-14 place-items-center rounded-2xl text-white shadow-lg"
                  style={{ background: "linear-gradient(135deg,#0A2540 0%,#10B981 160%)" }}
                >
                  <s.i className="h-7 w-7" />
                </div>
                <div className="font-serif text-5xl font-semibold text-emerald-500/25 leading-none">{s.num}</div>
              </div>
              <h3 className="font-serif mt-5 text-2xl text-[#0A2540]">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.d}</p>
              <ul className="mt-5 flex-1 space-y-2 text-sm text-slate-700">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-slate-100 pt-4">
                <Button
                  onClick={openPopup}
                  className="h-11 w-full rounded-full text-sm font-semibold text-white"
                  style={{ background: "linear-gradient(135deg,#0A2540 0%,#10B981 165%)" }}
                  data-testid={`lp-service-cta-${s.num}`}
                >
                  {s.cta} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </MobileBoxRow>
      </section>

      {/* Section 5 — Proof points */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="lp-sec-proof">
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 sm:p-12">
          <MobileBoxRow cols="sm:grid-cols-4" gap="gap-8" className="text-center">
            {[{v:"400+",l:"Pages live"},{v:"92%",l:"AfC acceptance"},{v:"42",l:"Languages"},{v:"11yrs",l:"Editor tenure"}].map((s)=>(
              <div key={s.l}><div className="font-serif text-4xl font-semibold text-[#0A2540] sm:text-5xl">{s.v}</div><div className="mt-2 text-sm text-slate-500">{s.l}</div></div>
            ))}
          </MobileBoxRow>
          <div className="mt-8 text-center"><SectionCTA label="See the numbers in context" testId="lp-cta-5" variant="outline" /></div>
        </div>
      </section>

      {/* Section 6 — Process */}
      <section className="bg-slate-50 py-16" data-testid="lp-sec-process">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Process</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">From first call to live page.</h2></div>
          <MobileBoxRow cols="md:grid-cols-4" gap="gap-5" className="mt-10">
            {[{n:"01",t:"Review"},{n:"02",t:"Research"},{n:"03",t:"Draft"},{n:"04",t:"Publish"}].map((p)=>(
              <div key={p.n} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="font-serif text-4xl text-emerald-500/50">{p.n}</div>
                <h3 className="font-serif mt-2 text-lg text-[#0A2540]">{p.t}</h3>
                <p className="mt-1 text-sm text-slate-600">Transparent updates at every stage until the page is live on Wikipedia.</p>
              </div>
            ))}
          </MobileBoxRow>
          <div className="mt-8"><SectionCTA label="Walk me through the process" testId="lp-cta-6" /></div>
        </div>
      </section>

      {/* Section 7 — Sources we accept (redesigned as tiered visual hierarchy) */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="lp-sec-sources">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">The source hierarchy</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Only the sources Wikipedia truly trusts.</h2>
          <p className="mt-4 text-base text-slate-600">Reviewers grade every citation. A single weak source can sink an otherwise strong draft. Here's the hierarchy we work within — and the one your competitors often ignore.</p>
        </div>

        <MobileBoxRow cols="lg:grid-cols-3" gap="gap-5" className="mt-12">
          {/* Tier 1 — Accepted */}
          <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-7 shadow-[0_25px_60px_-35px_rgba(16,185,129,0.5)]">
            <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
              <CheckCircle2 className="h-3 w-3" /> Accepted
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500 text-white shadow-lg">
              <Award className="h-5 w-5" />
            </div>
            <div className="mt-5 font-serif text-2xl text-[#0A2540]">Tier 1 · Gold standard</div>
            <p className="mt-1.5 text-sm text-slate-600">Independent, editorially-governed, fact-checked. Carry the most weight in every AfC review.</p>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {["Reuters", "AP", "BBC", "NYT", "WSJ", "FT", "Bloomberg", "The Guardian", "AFP"].map((s) => (
                <span key={s} className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-medium text-emerald-800">{s}</span>
              ))}
            </div>
            <ul className="mt-5 space-y-1.5 text-xs text-slate-600">
              <li className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500"/>Peer-reviewed academic journals</li>
              <li className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500"/>Books from established imprints</li>
            </ul>
          </div>

          {/* Tier 2 — Conditional */}
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-7">
            <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
              <HelpCircle className="h-3 w-3" /> Conditional
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-500 text-white shadow-lg">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="mt-5 font-serif text-2xl text-[#0A2540]">Tier 2 · Case-by-case</div>
            <p className="mt-1.5 text-sm text-slate-600">Usable in specific contexts — reviewers may challenge them unless paired with a Tier 1 source.</p>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {["TechCrunch", "Forbes (staff)", "Inc.", "Fast Company", "Billboard", "Variety", "Deadline"].map((s) => (
                <span key={s} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">{s}</span>
              ))}
            </div>
            <ul className="mt-5 space-y-1.5 text-xs text-slate-600">
              <li className="flex items-start gap-1.5"><HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500"/>Niche trade publications</li>
              <li className="flex items-start gap-1.5"><HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500"/>Regional news with editorial desk</li>
            </ul>
          </div>

          {/* Tier 3 — Rejected */}
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-7">
            <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
              <AlertCircle className="h-3 w-3" /> Rejected
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-400 text-white shadow-lg">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div className="mt-5 font-serif text-2xl text-[#0A2540]">Tier 3 · Never cited</div>
            <p className="mt-1.5 text-sm text-slate-600">The sources that get pages deleted on sight. We never use them — and neither should your last agency.</p>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {["Press releases", "Forbes Contributors", "Paid PR wires", "LinkedIn", "Medium", "Own website", "Interviews"].map((s) => (
                <span key={s} className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 line-through decoration-rose-400">{s}</span>
              ))}
            </div>
            <ul className="mt-5 space-y-1.5 text-xs text-slate-600">
              <li className="flex items-start gap-1.5"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500"/>Sponsored / pay-to-play content</li>
              <li className="flex items-start gap-1.5"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500"/>User-generated or self-published</li>
            </ul>
          </div>
        </MobileBoxRow>

        <div className="mt-10 text-center">
          <SectionCTA label="Audit my sources free" testId="lp-cta-7" />
        </div>
      </section>

      {/* Section 8 — Editorial Guardrails (conceptual, not step-based) */}
      <section className="relative overflow-hidden bg-[#0A2540] py-24 text-white" data-testid="lp-sec-guardrails">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #10B981 0, transparent 45%), radial-gradient(circle at 85% 80%, #3B82F6 0, transparent 45%)" }} />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-start">
            {/* Left — headline & philosophy */}
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Editorial guardrails</p>
              <h2 className="font-serif mt-3 text-3xl font-semibold leading-[1.1] sm:text-4xl lg:text-[2.7rem]">The principles behind every page we publish.</h2>
              <p className="mt-5 text-base leading-relaxed text-white/75">
                Wikipedia pages don't fail from a lack of effort — they fail from a single missed principle. We don't treat Wikipedia as a marketing channel or a traffic source; we treat it as the encyclopedic record it is, and we hold every word we contribute to that standard.
              </p>
              <p className="mt-4 text-base leading-relaxed text-white/75">
                These aren't steps on a checklist. They're the convictions that shape every draft, every source decision and every line of text we send to a reviewer.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-500 text-white"><ShieldCheck className="h-5 w-5" /></div>
                <div>
                  <div className="font-serif text-base">98.7% stay live past year two</div>
                  <div className="text-xs text-white/60">Audited against 140 agency-built pages</div>
                </div>
              </div>
              <div className="mt-8"><SectionCTA label="Get an expert verdict" testId="lp-cta-8" /></div>
            </div>

            {/* Right — conceptual principles (no numbers, no stages) */}
            <div className="min-w-0 lg:col-span-7">
              <MobileBoxRow cols="sm:grid-cols-2" gap="gap-5">
                {[
                  {
                    i: ShieldCheck,
                    t: "Policy-first, not press-first",
                    d: "Every sentence is held to Wikipedia's verifiability and neutrality standards before it ever touches your marketing goals.",
                  },
                  {
                    i: FileText,
                    t: "Sources over opinions",
                    d: "One independent, secondary citation outweighs a hundred self-described facts. Evidence — not narrative — is what survives review.",
                  },
                  {
                    i: Quote,
                    t: "The encyclopedic voice",
                    d: "Reviewers reject promotional tone on sight. We write the way a careful editor with no vested interest would write it.",
                  },
                  {
                    i: Lock,
                    t: "Built to be defensible",
                    d: "A page is only as valuable as its ability to stay up. We draft with the next ten reviewers in mind, not just the first.",
                  },
                ].map((p) => (
                  <div key={p.t} className="group relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-emerald-300/40 hover:bg-white/[0.07]">
                    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30">
                      <p.i className="h-5 w-5" />
                    </div>
                    <h3 className="font-serif mt-4 text-lg leading-tight text-white">{p.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">{p.d}</p>
                  </div>
                ))}
              </MobileBoxRow>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9 — Who it's for: removed per spec */}

      {/* Section 10 — Guarantees */}
      <section className="bg-slate-50 py-16" data-testid="lp-sec-guarantees">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Guarantees</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Three commitments we make to you.</h2></div>
          <MobileBoxRow cols="md:grid-cols-3" gap="gap-5" className="mt-10">
            {[{i:Lock,t:"NDA on every project",d:"Your identity is never linked to our agency publicly."},{i:ShieldCheck,t:"Refund if not notable",d:"Deposit returned if we determine you don't qualify."},{i:Clock,t:"12 months monitoring",d:"Included with Signature and Enterprise engagements."}].map((g)=>(
              <div key={g.t} className="rounded-2xl border border-slate-200 bg-white p-6"><g.i className="h-5 w-5 text-emerald-500"/><h3 className="font-serif mt-3 text-lg text-[#0A2540]">{g.t}</h3><p className="mt-2 text-sm text-slate-600">{g.d}</p></div>
            ))}
          </MobileBoxRow>
          <div className="mt-8 text-center"><SectionCTA label="Claim my discount" testId="lp-cta-10" /></div>
        </div>
      </section>

      {/* Section 11 — Standardised testimonial carousel */}
      <TestimonialCarousel />

      {/* Section 12 — FAQ */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="lp-sec-faq">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">FAQ</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Quick answers before you submit.</h2>
            <p className="mt-3 max-w-xl text-base text-slate-600">The most common questions people ask before filling the form — so you can decide whether we're the right fit before you share a single detail.</p>
            <Accordion type="single" collapsible className="mt-8 space-y-3">
              {[
                {q:"Do you guarantee publication?",a:"We don't guarantee publication — no ethical agency can. We do guarantee a thorough notability review and a refund if you're not eligible."},
                {q:"How long does it take?",a:"It depends on source availability and reviewer queues. You'll get a clear, week-by-week plan after the initial audit."},
                {q:"Is everything confidential?",a:"Yes. NDA by default, neutral editor accounts, and no traces linking edits back to your brand."},
                {q:"What if my page is deleted later?",a:"Our Signature plan includes 12 months of monitoring and free re-defence if the page is challenged."},
              ].map((i,idx)=>(
                <AccordionItem key={idx} value={`lp-${idx}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white px-6"><AccordionTrigger className="text-left text-base font-semibold text-[#0A2540]">{i.q}</AccordionTrigger><AccordionContent className="text-sm text-slate-600">{i.a}</AccordionContent></AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8"><SectionCTA label="Ask a live specialist" testId="lp-cta-12" variant="outline" onClick={() => window.OpenChat && window.OpenChat()} /></div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative h-full min-h-[440px] overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80"
                alt="A team discussing a Wikipedia project around a shared laptop"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-[#0A2540]/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Talk to a human</div>
                <div className="mt-1 font-serif text-base text-[#0A2540]">Senior specialists reply within two hours — no sales bots, ever.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 13 — Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="lp-sec-final-cta">
        <div className="relative overflow-hidden rounded-3xl p-8 text-white sm:p-12" style={{background:"linear-gradient(135deg, #0A2540 0%, #123B6E 55%, #10B981 180%)"}}>
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-30" style={{backgroundImage:"radial-gradient(circle at 85% 20%, rgba(16,185,129,0.5) 0, transparent 40%), radial-gradient(circle at 10% 90%, rgba(59,130,246,0.5) 0, transparent 40%)"}} />
          <div className="relative max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Limited slots this quarter</span>
            <h2 className="font-serif mt-4 text-3xl font-semibold leading-[1.1] sm:text-4xl md:text-5xl">Ready to reserve your 35% discount?</h2>
            <p className="mt-4 max-w-2xl text-sm text-white/80 sm:text-base">Fill the form at the top of the page, or book a free 20-minute consult with a senior Wikipedian.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button onClick={openPopup} className="h-12 rounded-full bg-emerald-500 px-6 text-sm font-semibold text-white hover:bg-emerald-400" data-testid="lp-cta-13">Claim discount <ArrowRight className="ml-2 h-4 w-4" /></Button>
              <ChatButton className="h-12 rounded-full border border-white/30 bg-white/10 px-6 text-sm font-semibold text-white hover:bg-white/20"><MessageCircle className="h-4 w-4"/> Chat with a Specialist</ChatButton>
            </div>
          </div>
        </div>
      </section>

      {/* Compact footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <p>© {new Date().getFullYear()} Prime Wiki Services. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="hover:text-slate-800" data-testid="lp-footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-slate-800" data-testid="lp-footer-terms-link">Terms &amp; Conditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
